import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, LoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import { Box, TextField, Typography, Alert } from '@mui/material';

interface LocationPickerProps {
  onLocationSelect: (location: { lat: number; lng: number; address: string }) => void;
  initialLocation?: { lat: number; lng: number };
  apiKey: string;
}

const libraries: ("places")[] = ["places"];

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px'
};

const defaultCenter = {
  lat: 20.5937, // India center
  lng: 78.9629
};

export const LocationPicker: React.FC<LocationPickerProps> = ({
  onLocationSelect,
  initialLocation,
  apiKey
}) => {
  const [markerPosition, setMarkerPosition] = useState(initialLocation || defaultCenter);
  const [selectedAddress, setSelectedAddress] = useState('');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const onMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarkerPosition({ lat, lng });
      
      // Reverse geocode to get address
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const address = results[0].formatted_address;
          setSelectedAddress(address);
          onLocationSelect({ lat, lng, address });
        }
      });
    }
  }, [onLocationSelect]);

  const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMarkerPosition({ lat, lng });
      
      // Reverse geocode to get address
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const address = results[0].formatted_address;
          setSelectedAddress(address);
          onLocationSelect({ lat, lng, address });
        }
      });
    }
  }, [onLocationSelect]);

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const address = place.formatted_address || '';
        
        setMarkerPosition({ lat, lng });
        setSelectedAddress(address);
        onLocationSelect({ lat, lng, address });
      }
    }
  };

  const onAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY') {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Google Maps API Key Required
        </Typography>
        <Typography variant="caption">
          To use the location picker, please add your Google Maps API key to the .env file:
          <br />
          <code>REACT_APP_GOOGLE_MAPS_API_KEY=your_api_key_here</code>
          <br /><br />
          Get your API key from: <a href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noopener noreferrer">Google Cloud Console</a>
          <br />
          Enable: Maps JavaScript API and Places API
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <LoadScript googleMapsApiKey={apiKey} libraries={libraries}>
        <Box sx={{ mb: 2 }}>
          <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
            <TextField
              fullWidth
              placeholder="Search for your gym location..."
              value={selectedAddress}
              onChange={(e) => setSelectedAddress(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white'
                }
              }}
            />
          </Autocomplete>
        </Box>

        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={markerPosition}
          zoom={15}
          onClick={onMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            zoomControl: true
          }}
        >
          <Marker
            position={markerPosition}
            draggable={true}
            onDragEnd={onMarkerDragEnd}
          />
        </GoogleMap>

        {selectedAddress && (
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: '#14B8A6' }}>
            📍 {selectedAddress}
          </Typography>
        )}
      </LoadScript>
    </Box>
  );
};

export default LocationPicker;
